import { useEffect } from "react";
import { withTranslation } from "react-i18next";
import { compose } from "redux";
import { useIntl } from "react-intl";
import { Controller, useForm } from "react-hook-form";
import {
  Col,
  Form,
  FormGroup,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";
import { ErrorMessage } from "@hookform/error-message";
import classNames from "classnames";
import Toggle from "react-toggle";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import { getDemoMode } from "../../../../helpers";
import {
  useGetRevenueSettings,
  useUpdateRevenueSettings,
} from "../../../../swrHelpers/settings/revenueSettings";
import InvestorSubscriptionsList from "./InvestorSubscriptionsList";
import * as actions from "../../../../store/actions/index";

export const InvestorFees = () => {
  const dispatch = useDispatch();
  const demoMode = getDemoMode();
  const MySwal = withReactContent(Swal);
  const intl = useIntl();
  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ mode: "onChange" });
  const { payments } = useSelector((state) => state.paymentGateway);
  const { generalSettings } = useSelector((state) => state.settings);

  const { data: revenueSettings } = useGetRevenueSettings();

  const { trigger, isMutating } = useUpdateRevenueSettings();

  useEffect(() => {
    dispatch(actions.getPaymentGateway("stripe"));
  }, []);

  useEffect(() => {
    reset({
      id: revenueSettings?.id || undefined,
      investmentFeeStatus:
        revenueSettings?.investmentFeeStatus === "YES" || false,
      investmentPercentageFees: revenueSettings?.investmentPercentageFees || "",
      investmentFlatFees: revenueSettings?.investmentFlatFees || "",
      investorOperationFeeStatus:
        revenueSettings?.investorOperationFeeStatus === "YES" || false,
      investorOperationPercentageFees:
        revenueSettings?.investorOperationPercentageFees || "",
      investorOperationFlatFees:
        revenueSettings?.investorOperationFlatFees || "",
      investorDistributionFeeStatus:
        revenueSettings?.investorDistributionFeeStatus === "YES" || false,
      investorDistributionPercentageFees:
        revenueSettings?.investorDistributionPercentageFees || "",
      investorDistributionFixedFees:
        revenueSettings?.investorDistributionFixedFees || "",
      investorAnnualFeeStatus:
        revenueSettings?.investorAnnualFeeStatus === "YES" || false,
      investorSubscriptionDuration:
        revenueSettings?.investorSubscriptionDuration || "",
      walletAutoDeductStatus:
        revenueSettings?.walletAutoDeductStatus === "YES" || false,
      stripeAutoDeductStatus:
        revenueSettings?.stripeAutoDeductStatus === "YES" || false,
    });
  }, [revenueSettings]);

  const alertBox = (title, text, icon) => {
    MySwal.fire({
      title: title,
      text: text,
      icon: icon,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: true,
      timer: 3000,
    });
  };

  const onSubmit = async (data) => {
    data.investmentFeeStatus = data?.investmentFeeStatus ? "YES" : "NO";
    data.investorOperationFeeStatus = data?.investorOperationFeeStatus
      ? "YES"
      : "NO";
    data.investorDistributionFeeStatus = data?.investorDistributionFeeStatus
      ? "YES"
      : "NO";
    data.investorAnnualFeeStatus = data?.investorAnnualFeeStatus ? "YES" : "NO";
    data.walletAutoDeductStatus = data?.walletAutoDeductStatus ? "YES" : "NO";
    data.stripeAutoDeductStatus = data?.stripeAutoDeductStatus ? "YES" : "NO";

    try {
      const response = await trigger(data);

      if (response?.id) {
        alertBox("Fees Updated Successfully!", "", "success");
      } else {
        alertBox("Fees Update Failed!", "", "error");
      }
    } catch (error) {
      console.error("RevenueSettings update error:", error);
    }
  };

  return (
    <>
      <Card>
        <CardHeader title="Investor Fees" />
        <CardBody className="pt-0 px-4 py-6">
          <Row className="justify-content-md-center">
            <Col md={11}>
              <Form
                id="revenueSettings"
                className="form form-label-right"
                onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-6 bg-table-bg-clr p-4 rounded-fix">
                  <div className="form-group bg-white p-4 rounded-fix d-flex justify-content-between align-items-center">
                    <label className="text-black font-size-sm">
                      Investment Fees
                      <span className="text-important mx-1">*</span>
                      <OverlayTrigger
                        overlay={
                          <Tooltip id="pending-actions-tooltip">
                            Specify the percentage (%) and fixed ($) fees that
                            will be charged to investors for each investment
                            they make.
                          </Tooltip>
                        }>
                        <span className="tooltip-wrapper">
                          <i className="fa fa-question-circle text-black" />
                        </span>
                      </OverlayTrigger>
                    </label>
                    <Controller
                      name="investmentFeeStatus"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <Toggle
                          name="investmentFeeStatus"
                          icons={{ checked: null, unchecked: null }}
                          checked={field.value}
                          onChange={() => field.onChange(!field.value)}
                        />
                      )}
                    />
                  </div>
                  {watch("investmentFeeStatus") && (
                    <div className="row mt-5 gap-5 gap-md-0">
                      <div className="col-md-6 form-group">
                        <FormGroup className="px-4">
                          <Row>
                            <Col
                              xs={"auto"}
                              className="d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium">
                              <label id="percentageCampaignCommission">{`Percentage (%)`}</label>
                            </Col>
                            <Col className="p-0">
                              <Form.Control
                                className={classNames(
                                  "form-control rounded-0 rounded-end text-black bg-white",
                                  {
                                    "is-invalid":
                                      errors.investmentPercentageFees,
                                  }
                                )}
                                {...register("investmentPercentageFees", {
                                  required: "This field is required",
                                  pattern: {
                                    value: /^(\d+(\.\d*)?|\.\d+)$/, // ✅ allows 123, 123.45, .45
                                    message: "Please enter valid value",
                                  },
                                })}
                              />
                            </Col>
                          </Row>
                        </FormGroup>
                        <ErrorMessage
                          errors={errors}
                          name="investmentPercentageFees"
                          render={({ message }) => (
                            <div className="invalid-feedback d-block ms-1">
                              {message}
                            </div>
                          )}
                        />
                      </div>
                      <div className="col-md-6 form-group">
                        <FormGroup className="px-4">
                          <Row>
                            <Col
                              xs={"auto"}
                              className="d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium">
                              <label id="fixedCampaignCommission">{`Fixed (${generalSettings.currencyId?.symbol})`}</label>
                            </Col>
                            <Col className="p-0">
                              <Form.Control
                                name="investmentFlatFees"
                                className={classNames(
                                  "form-control rounded-0 rounded-end text-black bg-white",
                                  {
                                    "is-invalid": errors.investmentFlatFees,
                                  }
                                )}
                                {...register("investmentFlatFees", {
                                  required: "This field is required",
                                  pattern: {
                                    value: /^(\d+(\.\d*)?|\.\d+)$/, // ✅ allows 123, 123.45, .45
                                    message: "Please enter valid value",
                                  },
                                })}
                              />
                            </Col>
                          </Row>
                        </FormGroup>
                        <ErrorMessage
                          errors={errors}
                          name="investmentFlatFees"
                          render={({ message }) => (
                            <div className="invalid-feedback d-block ms-1">
                              {message}
                            </div>
                          )}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="mb-6 bg-table-bg-clr p-4 rounded-fix">
                  <div className="form-group bg-white p-4 rounded-fix d-flex justify-content-between align-items-center">
                    <label className="text-black font-size-sm">
                      Operations Fees
                      <span className="text-important mx-1">*</span>
                      <OverlayTrigger
                        overlay={
                          <Tooltip id="pending-actions-tooltip">
                            Specify the percentage (%) and fixed ($) fees that
                            will be charged to investors each time dividends are
                            distributed.
                          </Tooltip>
                        }>
                        <span className="tooltip-wrapper">
                          <i className="fa fa-question-circle text-black" />
                        </span>
                      </OverlayTrigger>
                    </label>
                    <Controller
                      name="investorOperationFeeStatus"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <Toggle
                          name="investorOperationFeeStatus"
                          icons={{ checked: null, unchecked: null }}
                          checked={field.value}
                          onChange={() => field.onChange(!field.value)}
                        />
                      )}
                    />
                  </div>
                  {watch("investorOperationFeeStatus") && (
                    <div className="row mt-5 gap-5 gap-md-0">
                      <div className="col-md-6 form-group">
                        <FormGroup className="px-4">
                          <Row>
                            <Col
                              xs={"auto"}
                              className="d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium">
                              <label id="percentageOperationFees">{`Percentage (%)`}</label>
                            </Col>
                            <Col className="p-0">
                              <Form.Control
                                className={classNames(
                                  "form-control rounded-0 rounded-end text-black bg-white",
                                  {
                                    "is-invalid":
                                      errors.investorOperationPercentageFees,
                                  }
                                )}
                                {...register(
                                  "investorOperationPercentageFees",
                                  {
                                    required: "This field is required",
                                    pattern: {
                                      value: /^(\d+(\.\d*)?|\.\d+)$/, // ✅ allows 123, 123.45, .45
                                      message: "Please enter valid value",
                                    },
                                  }
                                )}
                              />
                            </Col>
                          </Row>
                        </FormGroup>
                        <ErrorMessage
                          errors={errors}
                          name="investorOperationPercentageFees"
                          render={({ message }) => (
                            <div className="invalid-feedback d-block ms-1">
                              {message}
                            </div>
                          )}
                        />
                      </div>
                      <div className="col-md-6 form-group">
                        <FormGroup className="px-4">
                          <Row>
                            <Col
                              xs={"auto"}
                              className="d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium">
                              <label id="fixedOperationFees">{`Fixed (${generalSettings.currencyId?.symbol})`}</label>
                            </Col>
                            <Col className="p-0">
                              <Form.Control
                                className={classNames(
                                  "form-control rounded-0 rounded-end text-black bg-white",
                                  {
                                    "is-invalid":
                                      errors.investorOperationFlatFees,
                                  }
                                )}
                                {...register("investorOperationFlatFees", {
                                  required: "This field is required",
                                  pattern: {
                                    value: /^(\d+(\.\d*)?|\.\d+)$/, // ✅ allows 123, 123.45, .45
                                    message: "Please enter valid value",
                                  },
                                })}
                              />
                            </Col>
                          </Row>
                        </FormGroup>
                        <ErrorMessage
                          errors={errors}
                          name="investorOperationFlatFees"
                          render={({ message }) => (
                            <div className="invalid-feedback d-block ms-1">
                              {message}
                            </div>
                          )}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="mb-6 bg-table-bg-clr p-4 rounded-fix">
                  <div className="form-group bg-white p-4 rounded-fix d-flex justify-content-between align-items-center">
                    <label className="text-black font-size-sm">
                      Repayment / Distribution Fees
                      <span className="text-important mx-1">*</span>
                      <OverlayTrigger
                        overlay={
                          <Tooltip id="pending-actions-tooltip">
                            Specify the percentage (%) and fixed ($) fees that
                            will be charged to investors each time repayments
                            are distributed.
                          </Tooltip>
                        }>
                        <span className="tooltip-wrapper">
                          <i className="fa fa-question-circle text-black" />
                        </span>
                      </OverlayTrigger>
                    </label>
                    <Controller
                      name="investorDistributionFeeStatus"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <Toggle
                          name="investorDistributionFeeStatus"
                          icons={{ checked: null, unchecked: null }}
                          checked={field.value}
                          onChange={() => field.onChange(!field.value)}
                        />
                      )}
                    />
                  </div>
                  {watch("investorDistributionFeeStatus") && (
                    <div className="row mt-5 gap-5 gap-md-0">
                      <div className="col-md-6 form-group">
                        <FormGroup className="px-4">
                          <Row>
                            <Col
                              xs={"auto"}
                              className="d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium">
                              <label id="investorDistributionPercentageFees">{`Percentage (%)`}</label>
                            </Col>
                            <Col className="p-0">
                              <Form.Control
                                className={classNames(
                                  "form-control rounded-0 rounded-end text-black bg-white",
                                  {
                                    "is-invalid":
                                      errors.investorDistributionPercentageFees,
                                  }
                                )}
                                {...register(
                                  "investorDistributionPercentageFees",
                                  {
                                    required: "This field is required",
                                    pattern: {
                                      value: /^(\d+(\.\d*)?|\.\d+)$/, // ✅ allows 123, 123.45, .45
                                      message: "Please enter valid value",
                                    },
                                  }
                                )}
                              />
                            </Col>
                          </Row>
                        </FormGroup>
                        <ErrorMessage
                          errors={errors}
                          name="investorDistributionPercentageFees"
                          render={({ message }) => (
                            <div className="invalid-feedback d-block ms-1">
                              {message}
                            </div>
                          )}
                        />
                      </div>
                      <div className="col-md-6 form-group">
                        <FormGroup className="px-4">
                          <Row>
                            <Col
                              xs={"auto"}
                              className="d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium">
                              <label id="investorDistributionFixedFees">{`Fixed (${generalSettings.currencyId?.symbol})`}</label>
                            </Col>
                            <Col className="p-0">
                              <Form.Control
                                className={classNames(
                                  "form-control rounded-0 rounded-end text-black bg-white",
                                  {
                                    "is-invalid":
                                      errors.investorDistributionFixedFees,
                                  }
                                )}
                                {...register("investorDistributionFixedFees", {
                                  required: "This field is required",
                                  pattern: {
                                    value: /^(\d+(\.\d*)?|\.\d+)$/, // ✅ allows 123, 123.45, .45
                                    message: "Please enter valid value",
                                  },
                                })}
                              />
                            </Col>
                          </Row>
                        </FormGroup>
                        <ErrorMessage
                          errors={errors}
                          name="investorDistributionFixedFees"
                          render={({ message }) => (
                            <div className="invalid-feedback d-block ms-1">
                              {message}
                            </div>
                          )}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="mb-6 bg-table-bg-clr p-4 rounded-fix">
                  <div className="form-group bg-white p-4 rounded-fix d-flex justify-content-between align-items-center">
                    <label className="text-black font-size-sm">
                      Subscription Plans
                    </label>
                    <Controller
                      name="investorAnnualFeeStatus"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <Toggle
                          name="investorAnnualFeeStatus"
                          icons={{ checked: null, unchecked: null }}
                          checked={field.value}
                          onChange={() => field.onChange(!field.value)}
                        />
                      )}
                    />
                  </div>
                  {watch("investorAnnualFeeStatus") && (
                    <>
                      <div className="mt-5">
                        <div className="form-group bg-white p-4 rounded-fix d-flex justify-content-between align-items-center">
                          <span className="d-flex align-items-center justify-content-between">
                            <label className="text-black font-size-sm">
                              Subscription Duration
                            </label>
                          </span>
                          <div>
                            <div className="d-flex flex-wrap flex-md-nowrap gap-10">
                              <div className="custom-control custom-radio d-flex gap-2">
                                <Form.Check
                                  name="investorSubscriptionDuration"
                                  type="radio"
                                  id="investorSubscriptionDuration1"
                                  {...register("investorSubscriptionDuration", {
                                    required: "This field is required",
                                  })}
                                  className=""
                                  value="MONTHLY"
                                  onChange={() => {
                                    setValue(
                                      "investorSubscriptionDuration",
                                      "MONTHLY"
                                    );
                                  }}
                                />
                                <label
                                  title=""
                                  htmlFor="investorSubscriptionDuration1"
                                  className="custom-control-label">
                                  MONTHLY
                                </label>
                              </div>
                              <div className="custom-control custom-radio d-flex gap-2">
                                <Form.Check
                                  name="investorSubscriptionDuration"
                                  type="radio"
                                  id="investorSubscriptionDuration2"
                                  {...register("investorSubscriptionDuration")}
                                  className=""
                                  value="QUARTERLY"
                                  onChange={() => {
                                    setValue(
                                      "investorSubscriptionDuration",
                                      "QUARTERLY"
                                    );
                                  }}
                                />
                                <label
                                  title=""
                                  htmlFor="investorSubscriptionDuration2"
                                  className="custom-control-label">
                                  QUARTERLY
                                </label>
                              </div>
                              <div className="custom-control custom-radio d-flex gap-2">
                                <Form.Check
                                  name="investorSubscriptionDuration"
                                  type="radio"
                                  id="investorSubscriptionDuration3"
                                  {...register("investorSubscriptionDuration")}
                                  className=""
                                  value="YEARLY"
                                  onChange={() => {
                                    setValue(
                                      "investorSubscriptionDuration",
                                      "YEARLY"
                                    );
                                  }}
                                />
                                <label
                                  title=""
                                  htmlFor="investorSubscriptionDuration3"
                                  className="custom-control-label">
                                  YEARLY
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                        <ErrorMessage
                          errors={errors}
                          name="investorSubscriptionDuration"
                          render={({ message }) => (
                            <div className="invalid-feedback d-block ms-1">
                              {message}
                            </div>
                          )}
                        />
                      </div>
                      {generalSettings?.walletModule === "yes" &&
                      !watch("stripeAutoDeductStatus") ? (
                        <div className="mt-5 form-group bg-white p-4 rounded-fix d-flex justify-content-between align-items-center">
                          <label className="text-black font-size-sm">
                            Wallet Auto Deduct
                            <span className="text-important mx-1">*</span>
                            <OverlayTrigger
                              overlay={
                                <Tooltip id="pending-actions-tooltip">
                                  When it's turned ON, the subscription plan
                                  will be auto renewed and amount deducted from
                                  user's wallet
                                </Tooltip>
                              }>
                              <span className="tooltip-wrapper">
                                <i className="fa fa-question-circle text-black" />
                              </span>
                            </OverlayTrigger>
                          </label>
                          <Controller
                            name="walletAutoDeductStatus"
                            control={control}
                            defaultValue={false}
                            render={({ field }) => (
                              <Toggle
                                name="walletAutoDeductStatus"
                                icons={{ checked: null, unchecked: null }}
                                checked={field.value}
                                onChange={() => field.onChange(!field.value)}
                              />
                            )}
                          />
                        </div>
                      ) : null}

                      {payments?.paymentType === "stripe" &&
                      payments?.status === "yes" &&
                      !watch("walletAutoDeductStatus") ? (
                        <div className="mt-5 form-group bg-white p-4 rounded-fix d-flex justify-content-between align-items-center">
                          <label className="text-black font-size-sm">
                            Stripe Auto Deduct
                            <span className="text-important ms-1">*</span>
                            <OverlayTrigger
                              overlay={
                                <Tooltip id="pending-actions-tooltip">
                                  When it's turned ON, the subscription plan
                                  will be auto renewed and amount deducted from
                                  user's card used on first subscription
                                </Tooltip>
                              }>
                              <span className="tooltip-wrapper">
                                <i className="fa fa-question-circle text-black" />
                              </span>
                            </OverlayTrigger>
                          </label>
                          <Controller
                            name="stripeAutoDeductStatus"
                            control={control}
                            defaultValue={false}
                            render={({ field }) => (
                              <Toggle
                                name="stripeAutoDeductStatus"
                                icons={{ checked: null, unchecked: null }}
                                checked={field.value}
                                onChange={() => field.onChange(!field.value)}
                              />
                            )}
                          />
                        </div>
                      ) : null}
                    </>
                  )}
                </div>

                {demoMode === "false" ? (
                  <button
                    type="submit"
                    disabled={isMutating}
                    className="btn btn-secondary common-save-btn mb-2">
                    {isMutating ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"></span>
                    ) : (
                      intl.formatMessage({ id: "SAVE" })
                    )}
                  </button>
                ) : null}
              </Form>
            </Col>
          </Row>
        </CardBody>
      </Card>
      {watch("investorAnnualFeeStatus") && (
        <InvestorSubscriptionsList
          generalSettings={generalSettings}
          demoMode={demoMode}
        />
      )}
    </>
  );
};
export default compose(withTranslation())(InvestorFees);
